import { useEffect, useMemo, useState } from 'react';
import { attendanceAPI } from '../services/api';
import { formatDate } from '../utils/helpers';
import { CalendarDays, UserRound } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const statusColors = {
  present: 'bg-emerald-100 text-emerald-700',
  late: 'bg-amber-100 text-amber-700',
  half_day: 'bg-orange-100 text-orange-700',
  partial: 'bg-violet-100 text-violet-700',
  off_schedule: 'bg-purple-100 text-purple-700',
  on_leave: 'bg-sky-100 text-sky-700',
  absent: 'bg-rose-100 text-rose-700',
  holiday: 'bg-blue-100 text-blue-700',
  not_scheduled: 'bg-slate-100 text-slate-700',
};

const StudentAttendance = () => {
  const { terminology } = useAuth();
  const [fromDate, setFromDate] = useState(() => {
    const value = new Date();
    value.setDate(1);
    return value.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fromDate || !toDate) {
      return;
    }

    setLoading(true);
    attendanceAPI.getMyAttendanceReport({ from_date: fromDate, to_date: toDate })
      .then((response) => setReport(response.data))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load your attendance'))
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);

  const summaryCards = useMemo(() => {
    if (!report?.summary) {
      return [];
    }

    return [
      { label: 'Scheduled Days', value: report.summary.total_expected_days, tone: 'text-slate-900' },
      { label: 'Attended', value: report.summary.attended_days, tone: 'text-emerald-700' },
      { label: 'Late', value: report.summary.late, tone: 'text-amber-700' },
      { label: 'Partial', value: report.summary.partial, tone: 'text-violet-700' },
      { label: 'Leave', value: report.summary.on_leave, tone: 'text-sky-700' },
      { label: 'Absent', value: report.summary.absent, tone: 'text-rose-700' },
      { label: 'Attendance %', value: `${report.summary.attendance_percentage}%`, tone: 'text-teal-700' },
    ];
  }, [report]);

  return (
    <div className="space-y-6">
      <div className="app-hero rounded-[2rem] px-6 py-6 md:px-8">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="app-kicker">My Attendance</p>
            <h1 className="mt-2 text-3xl font-semibold">See your attendance clearly</h1>
            <p className="mt-2 max-w-2xl text-sm text-white/75">
              Review your own attendance percentage, scheduled days, leave, and daily attendance history for any selected period.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/80">
              <span className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/60">
                <CalendarDays size={14} />
                From
              </span>
              <input
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                className="w-full bg-transparent text-sm font-medium text-white outline-none"
              />
            </label>

            <label className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/80">
              <span className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/60">
                <CalendarDays size={14} />
                To
              </span>
              <input
                type="date"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                className="w-full bg-transparent text-sm font-medium text-white outline-none"
              />
            </label>
          </div>
        </div>
      </div>

      {report?.student && (
        <div className="app-surface rounded-[1.75rem] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
              <UserRound size={22} />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">{report.student.full_name}</h2>
              <p className="mt-1 text-sm text-slate-500">
                {report.student.department_name || 'No department'} • {report.student.section_name || `No ${terminology.group.toLowerCase()}`}
              </p>
              <p className="mt-1 text-sm text-slate-500">
                {formatDate(`${report.from_date}T00:00:00`)} to {formatDate(`${report.to_date}T00:00:00`)}
              </p>
            </div>
          </div>
        </div>
      )}

      {summaryCards.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-7">
          {summaryCards.map((card) => (
            <div key={card.label} className="app-surface rounded-[1.5rem] p-5">
              <p className="text-sm text-slate-500">{card.label}</p>
              <p className={`mt-2 text-3xl font-semibold ${card.tone}`}>{card.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="app-surface rounded-[1.75rem] p-6">
        <div className="mb-4 flex items-center gap-2">
          <UserRound size={18} className="text-teal-600" />
          <h2 className="text-lg font-semibold text-slate-900">Daily attendance history</h2>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400">Loading your attendance...</div>
        ) : !report?.records?.length ? (
          <div className="py-16 text-center text-slate-400">No attendance history found for this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200/70 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-3 text-left">Date</th>
                  <th className="px-3 py-3 text-left">Day</th>
                  <th className="px-3 py-3 text-left">Status</th>
                  <th className="px-3 py-3 text-left">Periods</th>
                  <th className="px-3 py-3 text-left">{terminology.area}</th>
                  <th className="px-3 py-3 text-left">{terminology.leader}</th>
                  <th className="px-3 py-3 text-left">Location</th>
                  <th className="px-3 py-3 text-left">Scan</th>
                </tr>
              </thead>
              <tbody>
                {report.records.map((record) => (
                  <tr key={record.date} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-3 py-4 text-slate-700">{formatDate(`${record.date}T00:00:00`)}</td>
                    <td className="px-3 py-4 text-slate-600">{record.day_name}</td>
                    <td className="px-3 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusColors[record.status] || 'bg-slate-100 text-slate-700'}`}>
                        {record.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-slate-600">{record.attended_periods}/{record.scheduled_periods}</td>
                    <td className="px-3 py-4 text-slate-600">{record.subject_name || '—'}</td>
                    <td className="px-3 py-4 text-slate-600">{record.teacher_name || '—'}</td>
                    <td className="px-3 py-4 text-slate-600">{record.location_name || '—'}</td>
                    <td className="px-3 py-4 text-slate-600">
                      {record.scan_time
                        ? new Date(record.scan_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : 'No scan'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentAttendance;
